import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { messages, type ListSummary } from '@nesc/shared';
import { api, errorMessage, type SingleResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input, Select } from '@/components/ui/input';

/** 選択した企業を静的リストへ追加する */
export const AddToListDialog = ({
  open,
  onOpenChange,
  companyIds,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyIds: string[];
  onDone: () => void;
}) => {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [listId, setListId] = useState('');
  const [newName, setNewName] = useState('');

  const { data: lists } = useQuery({
    queryKey: qk.lists,
    queryFn: () => api.get<SingleResponse<ListSummary[]>>('/lists'),
    enabled: open,
  });

  const staticLists = lists?.data.filter((l) => l.type === 'static') ?? [];

  const mutation = useMutation({
    mutationFn: async () => {
      if (mode === 'new') {
        return api.post<SingleResponse<ListSummary>>('/lists', {
          name: newName,
          type: 'static',
          companyIds,
        });
      }
      return api.post(`/lists/${listId}/items`, { companyIds });
    },
    onSuccess: () => {
      toast.success(`${messages.common.countCompanies(companyIds.length)}をリストに追加しました`);
      void queryClient.invalidateQueries({ queryKey: qk.lists });
      onOpenChange(false);
      setNewName('');
      onDone();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{messages.list.addToList}</DialogTitle>
          <DialogDescription>
            {messages.common.countCompanies(companyIds.length)}を対象にします。
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          <fieldset className="flex gap-4">
            <legend className="sr-only">追加先</legend>
            {(
              [
                { value: 'existing', label: '既存のリスト' },
                { value: 'new', label: '新しいリスト' },
              ] as const
            ).map((option) => (
              <label key={option.value} className="flex min-h-touch items-center gap-1.5 text-body">
                <input
                  type="radio"
                  name="list-mode"
                  checked={mode === option.value}
                  onChange={() => setMode(option.value)}
                  className="size-4 accent-foreground"
                />
                {option.label}
              </label>
            ))}
          </fieldset>

          {mode === 'existing' ? (
            <Field label="リスト" required>
              {(props) => (
                <Select {...props} value={listId} onChange={(e) => setListId(e.target.value)}>
                  <option value="">選択してください</option>
                  {staticLists.map((list) => (
                    <option key={list.id} value={list.id}>
                      {list.name}（{list.companyCount} 社）
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          ) : (
            <Field label="リスト名" required>
              {(props) => (
                <Input
                  {...props}
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="食品卸_東京"
                />
              )}
            </Field>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" size="lg" onClick={() => onOpenChange(false)}>
            {messages.common.cancel}
          </Button>
          <Button
            size="lg"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || (mode === 'existing' ? !listId : !newName.trim())}
          >
            {messages.common.add}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
